/**
 * NOTE (Yuxin, 2026-02-02): Modifier to Natural Language Translation
 *
 * This module provides utilities to translate structured modifier values
 * into natural language instructions that can be understood by Groq.
 *
 * The goal is to take programmatic style changes (e.g., { type: "textColor", value: "#ff0000" })
 * and convert them into human-readable instructions ("Change the text color to #ff0000").
 */

import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import { exhaustiveSwitch } from "replo-utils/lib/misc";

// =============================================================================
// Types
// =============================================================================

export interface StructuredModifier {
  type:
    | "textColor"
    | "backgroundColor"
    | "fontSize"
    | "fontWeight"
    | "fontFamily"
    | "lineHeight"
    | "letterSpacing"
    | "textAlign"
    | "padding"
    | "paddingTop"
    | "paddingRight"
    | "paddingBottom"
    | "paddingLeft"
    | "margin"
    | "marginTop"
    | "marginRight"
    | "marginBottom"
    | "marginLeft"
    | "borderRadius"
    | "borderColor"
    | "borderWidth"
    | "borderStyle"
    | "opacity"
    | "width"
    | "height"
    | "maxWidth"
    | "maxHeight"
    | "minWidth"
    | "minHeight"
    | "gap"
    | "boxShadow";
  value: string;
}

// =============================================================================
// Modifier Translation
// =============================================================================

/**
 * Translates a structured modifier to a natural language instruction
 */
export function describeModifier(modifier: StructuredModifier): string {
  return exhaustiveSwitch(
    modifier,
    "type",
  )({
    textColor: () => `Change the text color to ${modifier.value}`,
    backgroundColor: () => `Change the background color to ${modifier.value}`,
    fontSize: () => `Change the font size to ${modifier.value}`,
    fontWeight: () => `Change the font weight to ${modifier.value}`,
    fontFamily: () => `Change the font family to ${modifier.value}`,
    lineHeight: () => `Set the line height to ${modifier.value}`,
    letterSpacing: () => `Set the letter spacing to ${modifier.value}`,
    textAlign: () => `Align the text to ${modifier.value}`,
    borderColor: () => `Change the border color to ${modifier.value}`,
    opacity: () => `Set the opacity to ${modifier.value}`,
    padding: () => `Set the padding to ${modifier.value}`,
    paddingTop: () => `Set the top padding to ${modifier.value}`,
    paddingRight: () => `Set the right padding to ${modifier.value}`,
    paddingBottom: () => `Set the bottom padding to ${modifier.value}`,
    paddingLeft: () => `Set the left padding to ${modifier.value}`,
    margin: () => `Set the margin to ${modifier.value}`,
    marginTop: () => `Set the top margin to ${modifier.value}`,
    marginRight: () => `Set the right margin to ${modifier.value}`,
    marginBottom: () => `Set the bottom margin to ${modifier.value}`,
    marginLeft: () => `Set the left margin to ${modifier.value}`,
    gap: () => `Set the gap between children to ${modifier.value}`,
    borderRadius: () => `Set the border radius to ${modifier.value}`,
    borderWidth: () => `Set the border width to ${modifier.value}`,
    borderStyle: () => `Set the border style to ${modifier.value}`,
    width: () => `Set the width to ${modifier.value}`,
    height: () => `Set the height to ${modifier.value}`,
    maxWidth: () => `Set the max width to ${modifier.value}`,
    maxHeight: () => `Set the max height to ${modifier.value}`,
    minWidth: () => `Set the min width to ${modifier.value}`,
    minHeight: () => `Set the min height to ${modifier.value}`,
    boxShadow: () => `Add a box shadow: ${modifier.value}`,
  });
}

// =============================================================================
// UniversalModifierValue (schema) Translation
// =============================================================================

/**
 * Converts a schema UniversalModifierValue to a natural language instruction.
 * Used when integrating with existing modifier blocks that use schema types.
 */
export function describeUniversalModifier(
  modifier: UniversalModifierValue,
): string {
  return exhaustiveSwitch(modifier)({
    color: (m) => {
      return m.value != null
        ? describeModifier({ type: "textColor", value: m.value })
        : "Remove the text color";
    },
    backgroundColor: (m) => {
      return m.value != null
        ? describeModifier({ type: "backgroundColor", value: m.value })
        : "Remove the background color";
    },
    borderColor: (m) => {
      return m.value != null
        ? describeModifier({ type: "borderColor", value: m.value })
        : "Remove the border color";
    },
    fontSize: (m) => {
      return m.value != null
        ? describeModifier({ type: "fontSize", value: `${m.value}px` })
        : "Reset the font size";
    },
    fontWeight: (m) => {
      return describeModifier({ type: "fontWeight", value: String(m.value) });
    },
    fontFamily: (m) => describeModifier({ type: "fontFamily", value: m.value }),
    lineHeight: (m) => {
      return m.value != null
        ? describeModifier({ type: "lineHeight", value: String(m.value) })
        : "Reset the line height";
    },
    textSpacing: (m) => {
      return m.value != null
        ? describeModifier({ type: "letterSpacing", value: `${m.value}em` })
        : "Reset the letter spacing";
    },
    alignment: (m) => describeModifier({ type: "textAlign", value: m.value }),
    content: (m) => `Change the text content to "${m.value}"`,
    padding: (m) => {
      const paddingTypes = {
        top: "paddingTop",
        right: "paddingRight",
        bottom: "paddingBottom",
        left: "paddingLeft",
      } as const;
      return describeModifier({
        type: paddingTypes[m.direction],
        value: `${m.value}px`,
      });
    },
    margin: (m) => {
      const marginTypes = {
        top: "marginTop",
        right: "marginRight",
        bottom: "marginBottom",
        left: "marginLeft",
      } as const;
      return describeModifier({
        type: marginTypes[m.direction],
        value: m.value === "auto" ? "auto" : `${m.value}px`,
      });
    },
    opacity: (m) => {
      return describeModifier({ type: "opacity", value: String(m.value) });
    },
    borderStyle: (m) => {
      return describeModifier({ type: "borderStyle", value: m.value });
    },
    borderWidth: (m) => {
      return `Set the ${m.direction} border width to ${m.value}px`;
    },
    cornerRadius: (m) => {
      const cornerLabels = {
        topLeft: "top-left",
        topRight: "top-right",
        bottomRight: "bottom-right",
        bottomLeft: "bottom-left",
      } as const;
      const radiusVal = m.value === "full" ? "9999px" : `${m.value}px`;
      return `Set the ${cornerLabels[m.corner]} border radius to ${radiusVal}`;
    },
    boxShadow: (m) => describeModifier({ type: "boxShadow", value: m.value }),
    containerDirection: (m) => `Set the flex direction to ${m.value}`,
    containerAlignment: (m) => `Align items to ${m.value}`,
    containerGap: (m) => {
      return describeModifier({ type: "gap", value: `${m.value}px` });
    },
    dimension: (m) => {
      const dimLookup: Record<string, string> = {
        full: "100%",
        auto: "auto",
      };
      const dimVal = dimLookup[m.value] ?? `${m.value}px`;
      return describeModifier({
        type: m.side === "width" ? "width" : "height",
        value: dimVal,
      });
    },
    objectFit: (m) => `Set the object fit to ${m.value}`,
    aspectRatio: (m) => {
      return m.value === "auto"
        ? "Remove the aspect ratio constraint"
        : `Set the aspect ratio to ${m.value}`;
    },
    href: (m) => `Set the link URL to ${m.value}`,
    delete: () => "Delete this element",
    asset: (m) => {
      return m.value != null
        ? `Change the image source URL to ${String(m.value)}`
        : "Remove the image source";
    },
    product: (m) => (m.value != null ? `Apply: ${String(m.value)}` : ""),
    variant: (m) => (m.value != null ? `Apply: ${String(m.value)}` : ""),
    textTag: (m) => (m.value != null ? `Apply: ${String(m.value)}` : ""),
    textShadow: (m) => (m.value != null ? `Apply: ${String(m.value)}` : ""),
  });
}
